import { Injectable } from '@angular/core';
import { AppConstants } from '../app-constants.service';
import { AppState } from '../app.state';
import { BuergscheineApiService } from '../../generated/api/buergscheine/buergscheine.service';
import { Observable, tap } from 'rxjs';
import { Buergschein, BuergscheinLight, BuergscheinListe } from '../../generated/api/kreditportalBackendAPI.schemas';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class BuergscheineService {

    private readonly thisYear: number;

    private readonly maxBuergscheine: number = 170;

    constructor(
        private readonly appConstants: AppConstants,
        private readonly state: AppState,
        private readonly buergscheineApiService: BuergscheineApiService
    ) {
        this.thisYear = appConstants.thisYear;
    }

    getBuergscheine(vsnr: string, geschaeftsjahr?: number): Observable<BuergscheinListe> {
        if (geschaeftsjahr) {
            this.state.setLastGeschaeftsjahrDisplayed('' + geschaeftsjahr);
        }

        const initialResponse: BuergscheinListe = {
            anzahlBuergscheine: 0,
            buergscheine: []
        }

        return this.loadAndAssembleBuergscheineBulks(vsnr, geschaeftsjahr, initialResponse, 1);
    }

    getBuergscheinLight(vsnr: string, bsnr: string): Observable<BuergscheinLight> {
        const vsnrEncoded = this.getOrdnungsbegriffUrlEncoded(vsnr);

        const year = parseInt(this.state.getLastGeschaeftsjahrDisplayed()) || this.thisYear;

        return this.buergscheineApiService.getBuergscheine(vsnrEncoded, { geschaeftsjahr: String(year), startBuergscheinnummer: 1 }).pipe(
            map(response => response.buergscheine.find(b => b.buergscheinId === bsnr))
        );
    }

    getBuergschein(vsnr: string, bsnr: string): Observable<Buergschein> {
        const vsnrEncoded = this.getOrdnungsbegriffUrlEncoded(vsnr);
        const bsnrEncoded = this.getOrdnungsbegriffUrlEncoded(bsnr);

        return this.buergscheineApiService.getBuergschein(vsnrEncoded, bsnrEncoded);
    }

    getBuergscheinDokument(vsnr: string, bsnr: string, original = false): Observable<Blob> {
        const vsnrEncoded = this.getOrdnungsbegriffUrlEncoded(vsnr);
        const bsnrEncoded = this.getOrdnungsbegriffUrlEncoded(bsnr);

        return this.buergscheineApiService.getBuergscheinDokument(vsnrEncoded, bsnrEncoded, { original });
    }

    postBuergscheinRueckforderung(vsnr: string, bsnr: string): Observable<void> {
        const vsnrEncoded = this.getOrdnungsbegriffUrlEncoded(vsnr);
        const bsnrEncoded = this.getOrdnungsbegriffUrlEncoded(bsnr);

        return this.buergscheineApiService.postBuergscheinRueckforderung(vsnrEncoded, bsnrEncoded);
    }

    private loadAndAssembleBuergscheineBulks(
        vsnr: string,
        geschaeftsjahr: number,
        response: BuergscheinListe,
        naechsteBuergscheineNummer: number,
    ): Observable<BuergscheinListe> {
        return this.loadSingleBuergscheineBulk(vsnr, geschaeftsjahr, naechsteBuergscheineNummer).pipe(
            tap(result => {
                response.anzahlBuergscheine += result.anzahlBuergscheine;
                response.buergscheine = response.buergscheine.concat(result.buergscheine);

                // nachlesen
                if (result.anzahlBuergscheine === this.maxBuergscheine) {
                    // letzten Buergschein entfernen, da dieser Bestandteil des 2. Aufrufes ist.
                    response.anzahlBuergscheine -= 1;
                    const letzteBuergscheinId = response.buergscheine.pop().buergscheinId;
                    const naechsteStartBuergscheinnummer: number = +letzteBuergscheinId.substring(
                        0,
                        letzteBuergscheinId.indexOf('-')
                    );

                    return this.loadAndAssembleBuergscheineBulks(
                        vsnr,
                        geschaeftsjahr,
                        response,
                        naechsteStartBuergscheinnummer
                    );
                } else {
                    return response;
                }
            }
        ));
    }

    private loadSingleBuergscheineBulk(
        vsnr: string,
        geschaeftsjahr: number,
        startBuergscheinnummer = 1
    ): Observable<BuergscheinListe> {
        const vsnrEncoded = this.getOrdnungsbegriffUrlEncoded(vsnr);

        if (!geschaeftsjahr) {
            return this.buergscheineApiService.getBuergscheine(
                vsnrEncoded,
                { startBuergscheinnummer }
            );
        } else {
            return this.buergscheineApiService.getBuergscheine(
                vsnrEncoded,
                { geschaeftsjahr: String(geschaeftsjahr), startBuergscheinnummer }
            );
        }
    }

    /**
     * Leider ist Spring nicht schlau genug, um URL-Parameter mit Slash / zuzulassen.
     * Das wird zum Problem bei Ordnungsbegriffen die Slashes haben wie z.B. VSNR "B 572/202040" oder BSNR "1/15-1"
     * Daher wird der Slash umgewandelt zu Unterstrich _
     *
     * @param ordnungsbegriff Ordnungsbegriff mit ggf. Slash
     * @private
     */
    private getOrdnungsbegriffUrlEncoded(ordnungsbegriff: string) {
        return encodeURIComponent(ordnungsbegriff.replace('/', '_'));
    }

}
